import { useState } from "react";
import { createPortal } from "react-dom";
import { Maximize2, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  HighlightedTextarea,
  type PromptVariable,
} from "./highlighted-textarea";

export function ExpandableHighlightedTextarea({
  value,
  onChange,
  placeholder,
  readOnly,
  rows = 4,
  variables,
  title,
  className,
  singleLine = false,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  readOnly?: boolean;
  rows?: number;
  variables?: PromptVariable[];
  title: string;
  className?: string;
  singleLine?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <div className="relative">
        <HighlightedTextarea
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          readOnly={readOnly}
          rows={rows}
          variables={variables}
          singleLine={singleLine}
          className={cn("pr-9", className)}
        />
        <button
          type="button"
          onClick={() => setExpanded(true)}
          aria-label={`Expand ${title}`}
          title="Expand"
          className="absolute bottom-1 right-1 z-10 flex h-5 w-5 items-center justify-center rounded border border-border/70 bg-background/90 text-text-muted shadow-sm backdrop-blur-sm transition-colors hover:border-border hover:bg-surface-hover hover:text-text-primary"
        >
          <Maximize2 size={11} />
        </button>
      </div>

      {expanded &&
        createPortal(
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-6"
            onClick={() => setExpanded(false)}
          >
            <div
              className="flex max-h-[80vh] w-[min(760px,calc(100vw-3rem))] flex-col rounded-lg border border-border bg-surface shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
                <h3 className="text-sm font-semibold text-text-primary">
                  {title}
                  {readOnly ? " (read-only)" : ""}
                </h3>
                <button
                  type="button"
                  onClick={() => setExpanded(false)}
                  aria-label="Close"
                  className="flex h-7 w-7 items-center justify-center rounded text-text-muted transition-colors hover:bg-surface-hover hover:text-text-primary"
                >
                  <X size={15} />
                </button>
              </div>
              <div className="flex-1 overflow-auto p-4">
                <HighlightedTextarea
                  autoFocus
                  value={value}
                  onChange={onChange}
                  readOnly={readOnly}
                  placeholder={placeholder}
                  className="h-[52vh]"
                  variables={variables}
                />
              </div>
              <div className="flex justify-end border-t border-border px-4 py-2.5">
                <Button size="sm" onClick={() => setExpanded(false)}>
                  Done
                </Button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
